import { LitElement, css, html, nothing } from '@lit'
import * as paths from '@nova/paths'
import styles from '@nova/styles'
import { updateCartQuantity } from '@nova/utils'

class NovaQuantityInput extends LitElement {
  static properties = {
    key: {},
    label: {},
    initial: {
      type: Number,
    },
    value: {
      state: true,
    },
    min: {
      type: String,
      attribute: 'min',
    },
    max: {
      type: String,
      attribute: 'max',
    },
    limit: {
      type: String,
      attribute: 'limit',
    },
    showButtons: {
      type: Boolean,
      attribute: 'show-buttons',
      converter: (value) => value === "true",
    }
  }

  constructor() {
    super()

    this.broadcast = this.broadcast.bind(this)
    this.id = Math.random() * 100000
    this.min = '0'
    this.max = ''
    this.limit = ''
  }

  connectedCallback() {
    super.connectedCallback()

    this.inMiniCart = this.classList.contains('cart-item__quantity-input')
    this.inCart = this.classList.contains('cart-item__quantity')
    this.errorMessageEl = this.closest('.js-qty-container')?.querySelector('.js-qty-error-message')
    this.errorLimitMessageEl = this.closest('.js-qty-container')?.querySelector('.js-qty-error-message-limit')
    this.buyButton = document.querySelector('.js-product-buy-button')
    this.miniCartCheckoutButton = document.querySelector('nova-mini-cart')?.shadowRoot?.querySelector('.js-minicart-checkout')
    this.cartCheckoutButton = document.querySelector('.inline-cart')?.querySelector('sl-button[type="submit"]')
    this.textileTotalPriceElem = document.querySelector('.js-b2b-textile-calculated-price-total')

    this.value = this.initial

    if (!this.inCart && !this.inMiniCart) {
      this.updateContent = this.updateContent.bind(this)
      document.addEventListener(
        'nova:product:render-quantity-input',
        this.updateContent,
      )
    }

    if (this.inCart) {
      this.updateQtyInCart = this.updateQtyInCart.bind(this)
      document.addEventListener(
        'changeQuantityStartedInCart',
        this.updateContent,
      )
      document.addEventListener(
        'changedQuantityInCart',
        this.updateQtyInCart,
      )
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback()

    if (!this.inCart && !this.inMiniCart) {
      document.removeEventListener(
        'nova:product:render-quantity-input',
        this.updateContent,
      )
    }

    if (this.inCart) {
      document.removeEventListener(
        'changeQuantityStartedInCart',
        this.updateContent,
      )
      document.removeEventListener(
        'changedQuantityInCart',
        this.updateQtyInCart,
      )
    }
  }

  updateContent(event) {
    const newMax = event.detail.html?.getAttribute('max')
    if (newMax) {
      this.setAttribute('max', newMax)
      this.max = newMax
      if (this.value > Number(newMax)) {
        this.value = Number(newMax)
        this.broadcast()
      }
    }

    const newMin = event.detail.html?.getAttribute('min')
    if (newMin) {
      this.setAttribute('min', newMin)
      this.min = newMin
      if (this.value < Number(newMin)) {
        this.value = Number(newMin)
        this.broadcast()
      }
    }

    const newLimit = event.detail.html?.getAttribute('limit')
    if (newLimit) {
      this.limit = newLimit
      if (this.value > Number(newLimit)) {
        this.broadcast()
      }
    }
  }

  async updateQtyInCart(event) {
    const { key, value } = event.detail

    await updateCartQuantity(
      [
        {
          id: key,
          quantity: value,
        },
      ],
      false,
    )
    location.reload()
  }

  willUpdate(changedProperties) {
    if (changedProperties.has('initial')) {
      this.value = this.initial
    }
  }

  broadcast() {
    const $input = this.renderRoot.querySelector('input.input')

    if ($input) {
      $input.value = this.value
    }

    if (this.inMiniCart) {
      if (this.errorMessageEl) {
        if (this.value < Number(this.min)) {
          this.errorMessageEl.classList.remove('hidden')
          this.miniCartCheckoutButton.classList.add('checkout-disabled')
          return
        } else {
          this.errorMessageEl.classList.add('hidden')
          this.miniCartCheckoutButton.classList.remove('checkout-disabled')
        }
      }

      if (this.errorLimitMessageEl && this.limit) {
        if (this.value > Number(this.limit)) {
          this.errorLimitMessageEl.classList.remove('hidden')
          this.miniCartCheckoutButton.classList.add('checkout-disabled')
          return
        } else {
          this.errorLimitMessageEl.classList.add('hidden')
          this.miniCartCheckoutButton.classList.remove('checkout-disabled')
        }
      }

      this.dispatchEvent(
        new CustomEvent('changeQuantityStarted', {
          bubbles: true,
          composed: true,
          detail: {
            key: this.key,
            value: this.value,
          },
        }),
      )
    } else if (this.inCart) {
      if (this.errorMessageEl) {
        if (this.value < Number(this.min)) {
          this.errorMessageEl.classList.remove('hidden')
          this.cartCheckoutButton.setAttribute('disabled', true)
          return
        } else {
          this.errorMessageEl.classList.add('hidden')
          this.cartCheckoutButton.removeAttribute('disabled')
        }
      }

      if (this.errorLimitMessageEl && this.limit) {
        if (this.value > Number(this.limit)) {
          this.errorLimitMessageEl.classList.remove('hidden')
          this.cartCheckoutButton.setAttribute('disabled', true)
          return
        } else {
          this.errorLimitMessageEl.classList.add('hidden')
          this.cartCheckoutButton.removeAttribute('disabled')
        }
      }

      this.dispatchEvent(
        new CustomEvent('changedQuantityInCart', {
          bubbles: true,
          composed: true,
          detail: {
            key: this.key,
            value: this.value,
          },
        }),
      )
    } else {
      let hasError = false
      if (this.errorMessageEl) {
        if (this.value < Number(this.min)) {
          this.errorMessageEl.classList.remove('hidden')
          this.buyButton.setAttribute('disabled', true)
          if (this.textileTotalPriceElem) {
            this.textileTotalPriceElem.classList.add('hidden')
          }
          hasError = true
        } else {
          this.errorMessageEl.classList.add('hidden')
          this.buyButton.removeAttribute('disabled')
          if (this.textileTotalPriceElem) {
            this.textileTotalPriceElem.classList.remove('hidden')
          }
        }
      }

      if (this.errorLimitMessageEl && this.limit && !hasError) {
        if (this.value > Number(this.limit)) {
          this.errorLimitMessageEl.classList.remove('hidden')
          this.buyButton.setAttribute('disabled', true)
          if (this.textileTotalPriceElem) {
            this.textileTotalPriceElem.classList.add('hidden')
          }
        } else {
          this.errorLimitMessageEl.classList.add('hidden')
          this.buyButton.removeAttribute('disabled')
          if (this.textileTotalPriceElem) {
            this.textileTotalPriceElem.classList.remove('hidden')
          }
        }
      }

      this.dispatchEvent(
        new CustomEvent('changeQuantityStarted', {
          bubbles: true,
          composed: true,
          detail: {
            key: this.key,
            value: this.value,
          },
        }),
      )

      this.dispatchEvent(
        new CustomEvent('nova:product:b2b-textile-calculated-price', {
          bubbles: true,
          composed: true,
          detail: {
            value: this.value,
          },
        }),
      )
    }
  }

  increment() {
    if (this.max !== '' && this.value >= Number(this.max)) {
      return
    }
    this.value += 1
    this.broadcast()
  }

  decrement() {
    if (this.value <= Number(this.min)) {
      return
    }
    this.value -= 1
    this.broadcast()
  }

  override(e) {
    let value = Math.max(0, e.target.value)
    if (this.max !== '' && value > Number(this.max)) {
      value = this.max
    }
    this.value = value
    this.broadcast()
  }

  render() {
    return html`
      <div class="wrapper">
        ${this.showButtons ? html`<button @click=${this.decrement} name="decrease" class="decrease">
          ${paths.minus}
        </button>`: nothing}
        <label class="sr-only" for="Quantity-${this.id}">
          ${this.label | 'Quantity'}
        </label>
        <input
          id="Quantity-${this.id}"
          class="input"
          type="number"
          name="updates[]"
          value=${this.value}
          @change=${this.override}
          min=${this.min}
          max=${this.max}
        />
        ${this.showButtons ? html`<button @click=${this.increment} name="increase" class="increase">
          ${paths.plus}
        </button>`: nothing}
      </div>
    `
  }

  static styles = [
    styles.a11y,
    styles.variables,
    css`
      .wrapper {
        border: 1px solid var(--nova-color-grey-200);
        border-radius: 0;
        box-sizing: border-box;
        color: #000;
        position: relative;
        display: flex;
        height: var(--nova-forms-input-height);
      }
      .input {
        color: var(--nova-color-grey-600);
        text-align: center;
        border: 0;
        width: 100%;
        flex-grow: 1;
        -webkit-appearance: none;
        appearance: none;
        -moz-appearance: textfield;
        font: var(--nova-body-font);
	      letter-spacing: var(--nova-body-letter-spacing);
	      text-transform: var(--nova-body-text-transform);

        &:focus,
        &:focus-visible {
          outline: 0;
        }

        @media screen and (max-width: 767px) {
          font-size: 16px;
        }
      }
      button {
        height: var(--nova-button-height-s);
        width: calc(var(--nova-space-sm) + var(--nova-space-xs));
        flex-shrink: 0;
        border: 0;
        background-color: transparent;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        color: var(--nova-color-grey-300);
      }
      .decrease {
        padding: 0 var(--nova-space-2xs) 0 var(--nova-space-xs);
      }
      .increase {
        padding: 0 var(--nova-space-xs) 0 var(--nova-space-2xs);
      }
      button:hover {
        color: #000;
      }
      .input:-webkit-autofill,
      .input:-webkit-autofill:hover,
      .input:-webkit-autofill:active {
        box-shadow: 0 0 0 10rem var(--color-background) inset !important;
        -webkit-box-shadow: 0 0 0 10rem var(--color-background) inset !important;
      }
      .input::-webkit-outer-spin-button,
      .input::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
      }
      svg {
        pointer-events: none;
      }
      :host(.cart-item__quantity-input) {
        & button {
          width: calc(2 * var(--nova-space-xs));
        }

        .decrease {
          padding: 0 0 0 var(--nova-space-xs);
        }
        .increase {
          padding: 0 var(--nova-space-xs) 0 0;
        }
      }
      .unit {
        padidng-left: 5px;

      }
    `,
  ]
}

customElements.define('nova-quantity-input', NovaQuantityInput)
